import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ActivityFeedFilters.css';

const ActivityFeedFilters = ({
  searchTerm,
  setSearchTerm,
  dateRange,
  setDateRange,
  applyDatePreset,
  selectedEventTypes,
  setSelectedEventTypes,
  clearFilters,
  activeFilterCount,
  totalEvents,
  filteredCount
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Available event types for filtering
  const eventTypes = [
    { value: 'LOGIN', label: 'Login', icon: '🔐' },
    { value: 'LOGOUT', label: 'Logout', icon: '👋' },
    { value: 'HOLDING_CREATED', label: 'Holdings Created', icon: '✅' },
    { value: 'HOLDING_UPDATED', label: 'Holdings Updated', icon: '✏️' },
    { value: 'HOLDING_DELETED', label: 'Holdings Deleted', icon: '🗑️' },
    { value: 'HOLDING_SOLD', label: 'Holdings Sold', icon: '💸' },
    { value: 'PORTFOLIO_REFRESHED', label: 'Portfolio Refreshed', icon: '🔄' },
    { value: 'SNAPSHOT_CREATED', label: 'Snapshots Created', icon: '📸' },
    { value: 'SNAPSHOT_DELETED', label: 'Snapshots Deleted', icon: '🗑️' },
    { value: 'CONTRIBUTION_ADDED', label: 'Contributions', icon: '💰' },
    { value: 'WITHDRAWAL_ADDED', label: 'Withdrawals', icon: '💵' },
    { value: 'PRICE_UPDATE', label: 'Price Updates', icon: '💹' },
    { value: 'ERROR_OCCURRED', label: 'Errors', icon: '❌' }
  ];

  // Toggle event type selection
  const toggleEventType = (eventType) => {
    setSelectedEventTypes(prev => {
      if (prev.includes(eventType)) {
        return prev.filter(t => t !== eventType);
      } else {
        return [...prev, eventType];
      }
    });
  };

  // Select/deselect all event types
  const toggleAllEventTypes = () => {
    if (selectedEventTypes.length === eventTypes.length) {
      setSelectedEventTypes([]);
    } else {
      setSelectedEventTypes(eventTypes.map(et => et.value));
    }
  };

  // Date range presets
  const presets = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'alltime', label: 'All time' }
  ];

  return (
    <div className="activity-feed-filters">
      {/* Filter Header */}
      <div className="filter-header">
        <button
          className="collapse-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand filters' : 'Collapse filters'}
        >
          <span className="collapse-icon">{isCollapsed ? '▼' : '▲'}</span>
          <span className="filter-title">Filters</span>
          {activeFilterCount > 0 && (
            <span className="filter-badge">{activeFilterCount}</span>
          )}
        </button>

        {activeFilterCount > 0 && (
          <button
            className="clear-all-button"
            onClick={clearFilters}
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Filter Panel */}
      <div className={`filter-panel ${isCollapsed ? 'collapsed' : 'expanded'}`}>
        {/* Search Filter */}
        <div className="filter-section">
          <label className="filter-label">Search</label>
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search activity feed"
            />
            {searchTerm && (
              <button
                className="clear-search-button"
                onClick={() => setSearchTerm('')}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="filter-section">
          <label className="filter-label">Date Range</label>

          {/* Date Presets */}
          <div className="date-presets">
            {presets.map(preset => (
              <button
                key={preset.value}
                className="preset-button"
                onClick={() => applyDatePreset(preset.value)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom Date Range */}
          <div className="date-range-inputs">
            <div className="date-input-group">
              <label className="date-label">From</label>
              <DatePicker
                selected={dateRange.start}
                onChange={(date) => setDateRange(prev => ({ ...prev, start: date }))}
                selectsStart
                startDate={dateRange.start}
                endDate={dateRange.end}
                maxDate={new Date()}
                placeholderText="Start date"
                className="date-picker-input"
                dateFormat="MMM d, yyyy"
                isClearable
              />
            </div>
            <div className="date-input-group">
              <label className="date-label">To</label>
              <DatePicker
                selected={dateRange.end}
                onChange={(date) => setDateRange(prev => ({ ...prev, end: date }))}
                selectsEnd
                startDate={dateRange.start}
                endDate={dateRange.end}
                minDate={dateRange.start}
                maxDate={new Date()}
                placeholderText="End date"
                className="date-picker-input"
                dateFormat="MMM d, yyyy"
                isClearable
              />
            </div>
          </div>
        </div>

        {/* Event Type Filter */}
        <div className="filter-section">
          <div className="filter-label-row">
            <label className="filter-label">Event Types</label>
            <button
              className="toggle-all-button"
              onClick={toggleAllEventTypes}
            >
              {selectedEventTypes.length === eventTypes.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="event-type-grid">
            {eventTypes.map(eventType => (
              <button
                key={eventType.value}
                className={`event-type-chip ${selectedEventTypes.includes(eventType.value) ? 'selected' : ''}`}
                onClick={() => toggleEventType(eventType.value)}
                aria-label={`Filter by ${eventType.label}`}
                aria-pressed={selectedEventTypes.includes(eventType.value)}
              >
                <span className="chip-icon">{eventType.icon}</span>
                <span className="chip-label">{eventType.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-count">
        Showing <strong>{filteredCount}</strong> of <strong>{totalEvents}</strong> events
        {activeFilterCount > 0 && (
          <span className="filter-active-text"> (filtered)</span>
        )}
      </div>
    </div>
  );
};

export default ActivityFeedFilters;
